/**
 * Product Related Vue component
 * ------------------------------------------------------------------------------
 * Main template in sections/product__related.liquid
 *
 * Recommended products retrieved via Product Recommendations Ajax API
 * https://shopify.dev/docs/themes/ajax-api/reference/product-recommendations
 *
 * Example usage:
 * <product-related inline-template product-id="{{ product.id }}"/>
 */

import axios from 'axios'
import Cookies from 'js-cookie'

import VueTabs from '@/js/components/vue-tabs.vue'
import VueTab from '@/js/components/vue-tab.vue'
import KeenSlider from '@/js/components/keen-slider.vue'
import ProductItem from '@/js/components/product-item.js'

import { storefrontRequestConfig, STOREFRONT_URL } from '@/js/utils/api'
import { productQuery, transformGraphqlProduct, transformAjaxProduct } from '@/js/utils/data'

const COOKIE_RECENTLY_VIEWED = 'recently_viewed'

export default {
  components: {
    KeenSlider,
    VueTabs,
    VueTab,
    ProductItem
  },

  props: {
    productId: {
      type: String,
      required: true
    },

    recentlyViewedCount: {
      type: Number,
      required: false,
      default: 6
    },

    pageType: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      isLoading: true,
      recommendedProducts: [],
      recentlyViewedProducts: [],
      sliderOptions: {
        slides: {
          perView: 2,
          spacing: 30
        },
        breakpoints: {
          '(min-width: 768px)': {
            slides: {
              perView: 4,
              spacing: 30
            }
          }
        }
      }
    }
  },

  async created () {
    // Recommended products
    const recommendedPromise = this.productId ? this._getRecommendedProducts() : Promise.resolve()

    // Recently viewed products
    let recentPromise = []

    if (Cookies.get(COOKIE_RECENTLY_VIEWED)) {
      const recentlyViewed = JSON.parse(Cookies.get(COOKIE_RECENTLY_VIEWED))

      if (this.pageType === 'product') {
        if (!recentlyViewed.includes(this.productId)) {
          Cookies.set(COOKIE_RECENTLY_VIEWED, JSON.stringify([this.productId, ...recentlyViewed]), { expires: 7 })
        }
      }

      recentPromise = this._getRecentlyViewedProducts(recentlyViewed.slice(0, this.recentlyViewedCount))
    } else {
      if (this.pageType === 'product') {
        Cookies.set(COOKIE_RECENTLY_VIEWED, JSON.stringify([this.productId]), { expires: 7 })
      }
    }

    // Needs to be initialised in this order so that the tab order is correct
    await Promise.all([recommendedPromise, recentPromise]).then(data => {
      this.recommendedProducts = data[0]
      this.recentlyViewedProducts = data[1]
    }).catch(error => {
      console.log(error)
    })

    this.isLoading = false
  },

  methods: {
    _getRecentlyViewedProducts (productIds) {
      const graphqlIds = productIds.filter(productId => productId !== this.productId && productId.length > 0).map(id => `"gid://shopify/Product/${id}"`)

      const data = `{
        nodes(ids: [${graphqlIds}]) {
          id
          ... on Product {
            ${productQuery}
          }
        }
      }`

      // eslint-disable-next-line no-undef
      return axios.post(STOREFRONT_URL, data, storefrontRequestConfig).then(({ data }) => {
        // Remove products that aren't available to Storefront API
        const products = data.data.nodes.filter(node => node)

        return products.map(product => transformGraphqlProduct(product)).map(product => ({ ...product, sale: this.getSalePercent(product.compare_at_price, product.price) }))
      }).catch((error) => {
        console.log('Recently viewed products error:', error)
        throw error
      })
    },

    _getRecommendedProducts () {
      return axios.get(`/recommendations/products.json?product_id=${this.productId}`).then(({ data }) => {
        return data.products.map(product => transformAjaxProduct(product))
      }).catch((error) => {
        console.log('Recommended products', error)
        throw error
      })
    },

    getSalePercent (originalPrice, price) {
      return Math.floor(((originalPrice - price) / originalPrice) * 100)
    }
  }
}
